import React, { FC, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { StepComment } from "./StepComment";
import { StepLog } from "./StepLog";
import { StepMeasurements } from "./StepMeasurement";
import { StepTools } from "./StepTools";
import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { Translate } from "@/app/application/Translate";
import {
	getSurfaceBorderColor,
	getSurfaceBorderStyle,
} from "@/app/application/theme";
import { useUpdateStepStatus } from "@/features/procedures/api/checklist.mutations";
import {
	getColumnTypeClass,
	getRowTypeClass,
	getTextColorForBackground,
	transformColorForTheme,
} from "@/features/procedures/lib/checklist";
import {
	Checklist,
	ProcedureNodeStep,
	UpdateStepStatusRequest,
} from "@/features/procedures/model/types/checklist";
import { SuggestionResponse } from "@/features/procedures/model/procedure/ProcedureNodeStepComment";
import { CheckpointType } from "@/features/procedures/model/types/manualChecklist";
import { useChecklistView } from "@/features/procedures/ui/components/checklists/checklist/ChecklistViewContext";
import { ProcedureUpdateSuggestion } from "@/features/procedures/ui/components/procedures/ProcedureUpdateSuggestion";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import WarningIcon from "@mui/icons-material/Warning";
import {
	Box,
	Chip,
	IconButton,
	Stack,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import { type Theme } from "@mui/material/styles";

import { useFetchGroups } from "@/service/GroupQueries";
import { RouteParams } from "@/service/Utilities";

const manualChecklistGridColumns = {
	xs: "40px minmax(0, 1fr) 88px",
	md: "48px minmax(0, 1fr) minmax(140px, 220px) 56px 104px",
};

interface StepProps {
	activeSchemeId: number | undefined;
	step: ProcedureNodeStep;
	checklist?: Checklist;
	materialHandling?: boolean;
	showCommentsAndSuggestion?: boolean | undefined;
	highlightedStepId?: number | null;
	scrollContainerId?: string;
	isProcedurePage?: boolean;
	isActiveStep: boolean;
	commentIndicator?: { count: number; onClick: () => void };
}

/** Step card layout: collapsed = one-line scan, expanded = comments + improvements + actions */
export const Step: FC<StepProps> = (props) => {
	const {
		step,
		activeSchemeId,
		highlightedStepId,
		showCommentsAndSuggestion,
		scrollContainerId,
		isProcedurePage,
		isActiveStep,
		commentIndicator,
	} = props;

	const { id } = useParams<RouteParams>();
	const theme = useTheme();
	const { bodyFontSize, headingFontSize } = useChecklistView();
	const { data: groups } = useFetchGroups();
	const [autoMode, setAutoMode] = useState(false);
	const [showMeasurements, setShowMeasurements] = useState(false);
	const [showStepLog, setShowStepLog] = useState(false);
	const [isFeedbackExpanded, setIsFeedbackExpanded] = useState(true);
	const updateStepState = useUpdateStepStatus();
	const getStatusChipSx = (t: typeof theme, isSkipped: boolean) => ({
		height: 22,
		fontWeight: 600,
		textTransform: "uppercase",
		letterSpacing: "0.3px",
		bgcolor: isSkipped
			? t.palette.mode === "dark"
				? alpha(t.palette.grey[100], 0.16)
				: t.palette.grey[200]
			: t.palette.mode === "dark"
				? alpha(t.palette.warning.main, 0.24)
				: alpha(t.palette.warning.main, 0.15),
		color: isSkipped
			? t.palette.mode === "dark"
				? t.palette.grey[50]
				: t.palette.grey[800]
			: t.palette.mode === "dark"
				? t.palette.warning.light
				: t.palette.warning.dark,
		border: `1px solid ${alpha(
			isSkipped ? t.palette.grey[400] : t.palette.warning.main,
			t.palette.mode === "dark" ? 0.5 : 0.4,
		)}`,
		"& .MuiChip-icon": { color: "inherit" },
	});

	const stepIndex =
		props.checklist?.Steps.findIndex((x) => x.Id === step.Id) ?? -1;
	const isOddRow = stepIndex >= 0 && stepIndex % 2 === 0;
	const manualChecklistStepNumber =
		step.CheckpointType === CheckpointType.Info ||
		props.checklist === undefined ||
		props.checklist.ImportedChecklist ||
		stepIndex === -1
			? undefined
			: props.checklist.Steps.slice(0, stepIndex).filter(
					(s) => s.CheckpointType !== CheckpointType.Info,
				).length + 1;
	/** For table alignment: always show a step number (manual # or Index for imported) */
	const displayStepNumber =
		manualChecklistStepNumber ??
		(props.checklist?.ImportedChecklist &&
		step.CheckpointType !== CheckpointType.Info
			? step.Index
			: undefined);

	const isCompleted = step.Status === 1;

	const toggleShowMeasurements = () => {
		setShowMeasurements((v) => !v);
		setAutoMode(false);
	};
	const toggleShowStepLog = () => setShowStepLog((log) => !log);
	const toggleFeedbackExpanded = () => setIsFeedbackExpanded((v) => !v);

	const handleSkipStep = async () => {
		const request: UpdateStepStatusRequest = {
			ArchiveType: "Status",
			StepId: step.Id,
			ProcedureId: Number(id),
			SchemeId: activeSchemeId ?? 0,
			IsSkipped: !step.IsSkipped,
			Status: 0,
			Metadata: { ...step, Status: 0 },
		};
		try {
			await updateStepState(request);
		} catch (e) {
			AlertRequest.failed();
			console.error(e);
		}
	};

	const pendingSuggestions = (
		step.ProcedureUpdateSuggestions as SuggestionResponse[]
	).filter((suggestion) => {
		return suggestion.Status !== "Declined" && suggestion.Status !== "Closed";
	});
	const commentCount = step.Comments?.length ?? 0;
	const improvementCount = pendingSuggestions.length;
	const hasCommentsOrImprovements = commentCount > 0 || improvementCount > 0;
	const checklistProcedureId =
		(props.checklist as (Checklist & { ProcedureId?: number }) | undefined)
			?.ProcedureId ?? props.checklist?.procedureId;

	const isHighlighted = step.Id === highlightedStepId;

	const transformedInfoBackgroundColor = step.BackgroundColor
		? transformColorForTheme(
				step.BackgroundColor,
				theme.palette.mode === "dark",
			)
		: null;
	const isDark = theme.palette.mode === "dark";
	const stripedStepBackgroundColor = isDark
		? alpha(theme.palette.common.white, isOddRow ? 0.05 : 0.02)
		: alpha(theme.palette.common.black, isOddRow ? 0.045 : 0.015);
	const infoStepBackgroundColor = transformedInfoBackgroundColor
		? transformedInfoBackgroundColor
		: isDark
			? alpha(theme.palette.info.light, 0.16)
			: alpha(theme.palette.info.main, 0.1);
	const infoStepTextColor = transformedInfoBackgroundColor
		? getTextColorForBackground(transformedInfoBackgroundColor)
		: theme.palette.text.primary;
	const documentTitleSurfaceColor = isDark
		? alpha(theme.palette.common.white, 0.06)
		: alpha(theme.palette.common.black, 0.04);
	const importedDocumentTitleSurfaceColor = isDark
		? alpha(theme.palette.common.white, 0.08)
		: alpha(theme.palette.common.black, 0.06);

	const getRowStyle =
		step.CheckpointType === CheckpointType.Info
			? (t: Theme) => ({
					backgroundColor: `${infoStepBackgroundColor} !important`,
					borderWidth: ".5px 0 .5px 0",
					borderStyle: "dotted",
					borderColor: getSurfaceBorderColor(t, "subtle"),
				})
			: null;

	const stepRef = useRef<HTMLDivElement | null>(null);

	const hasMeasurement =
		step?.CheckpointType === CheckpointType.ManualMeasurement ||
		step?.CheckpointType === CheckpointType.SATOSLive;

	useEffect(() => {
		if (!isHighlighted || !stepRef.current) return;
		const container = scrollContainerId
			? document.getElementById(scrollContainerId)
			: null;
		if (container) {
			const cRect = container.getBoundingClientRect();
			const eRect = stepRef.current.getBoundingClientRect();
			const deltaTop = eRect.top - cRect.top;
			const offset = deltaTop - (container.clientHeight / 2 - eRect.height / 2);
			container.scrollTo({
				top: container.scrollTop + offset,
				behavior: "smooth",
			});
		} else {
			stepRef.current.scrollIntoView({
				behavior: "smooth",
				block: "center",
				inline: "nearest",
			});
		}
	}, [isHighlighted, scrollContainerId]);

	const transformedBackgroundColor = step.BackgroundColor
		? transformColorForTheme(
				step.BackgroundColor,
				theme.palette.mode === "dark",
			)
		: null;
	const importedStepBackgroundColor = transformedBackgroundColor
		? transformedBackgroundColor
		: isDark
			? alpha(theme.palette.common.black, 0.34)
			: alpha(theme.palette.common.white, 0.96);
	const importedStepTextColor = transformedBackgroundColor
		? getTextColorForBackground(transformedBackgroundColor)
		: isDark
			? theme.palette.grey[50]
			: theme.palette.grey[900];

	const stepBackgroundColor =
		transformedBackgroundColor || stripedStepBackgroundColor;
	const stepTextColor = transformedBackgroundColor
		? getTextColorForBackground(transformedBackgroundColor)
		: isDark
			? "#ffffff"
			: theme.palette.text.primary;
	const richContentTableSx = {
		"--step-table-border-color": getSurfaceBorderColor(theme, "strong"),
		color: "inherit",
		"& *": {
			color: "inherit",
		},
		"& [style*='color']": {
			color: "inherit !important",
		},
		"& table": {
			width: "100%",
			borderCollapse: "collapse",
			borderColor: "var(--step-table-border-color)",
			marginTop: theme.spacing(0.75),
			marginBottom: theme.spacing(0.75),
		},
		"& th, & td": {
			border: "1px solid var(--step-table-border-color)",
			padding: "8px 10px",
		},
		...(!isDark && {
			"& th": {
				fontWeight: 700,
				backgroundColor: `${alpha(theme.palette.common.black, 0.045)} !important`,
			},
			"& tr:nth-of-type(even) td": {
				backgroundColor: `${alpha(theme.palette.common.black, 0.025)} !important`,
			},
		}),
		...(isDark && {
			"& table, & thead, & tbody, & tfoot, & tr, & th, & td": {
				color: "inherit !important",
				backgroundColor: `${alpha(theme.palette.common.black, 0.2)} !important`,
			},
			"& [style*='background-color']": {
				backgroundColor: "transparent !important",
			},
			"& table [style*='background-color']": {
				backgroundColor: `${alpha(theme.palette.common.black, 0.2)} !important`,
				color: "inherit !important",
			},
			"& th": {
				fontWeight: 700,
				backgroundColor: `${alpha(theme.palette.common.white, 0.1)} !important`,
			},
			"& tr:nth-of-type(even) td": {
				backgroundColor: `${alpha(theme.palette.common.white, 0.03)} !important`,
			},
		}),
	};

	function renderFeedbackPanels() {
		const feedbackColumnCount =
			commentCount > 0 && improvementCount > 0
				? { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }
				: "minmax(0, 1fr)";

		return (
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: feedbackColumnCount,
					gap: 2,
					alignItems: "start",
				}}
			>
				{commentCount > 0 && (
					<Box sx={{ minWidth: 0 }}>
						<Typography
							variant="overline"
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								mb: 1,
								color: "text.secondary",
							}}
						>
							<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
							<Translate String="Procedure.Checklist.Comments">
								Comments
							</Translate>
							({commentCount})
						</Typography>
						<Stack spacing={1}>
							{step.Comments?.map((comment) => (
								<StepComment
									key={comment.Id}
									activeSchemeId={activeSchemeId || 0}
									comment={comment}
									step={step}
								/>
							))}
						</Stack>
					</Box>
				)}

				{improvementCount > 0 && (
					<Box sx={{ minWidth: 0 }}>
						<Typography
							variant="overline"
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
								mb: 1,
								color: "text.secondary",
							}}
						>
							<LightbulbOutlinedIcon sx={{ fontSize: 16 }} />
							<Translate String="Procedure.Checklist.Improvements">
								Improvements
							</Translate>
							({improvementCount})
						</Typography>
						<Stack spacing={1}>
							{pendingSuggestions.map((suggestion, suggestionIndex) => (
								<ProcedureUpdateSuggestion
									key={suggestion.Id ?? `suggestion-${suggestionIndex}`}
									activeSchemeId={props.activeSchemeId || 0}
									suggestion={suggestion}
									step={props.step}
									procedureIdProp={checklistProcedureId}
									checklistIdProp={props.activeSchemeId}
								/>
							))}
						</Stack>
					</Box>
				)}
			</Box>
		);
	}

	// Info rows (section headings) – keep original compact layout
	if (step.CheckpointType === CheckpointType.Info) {
		const isImportedEditPreview =
			Boolean(props.checklist?.ImportedChecklist) && !isProcedurePage;
		const isManualProcedureInfo =
			!props.checklist?.ImportedChecklist && Boolean(isProcedurePage);
		const canToggleInfoFeedback =
			showCommentsAndSuggestion && hasCommentsOrImprovements;
		return (
			<Box
				className="grid-row grid-row-heading grid-row-heading-info"
				id={String(step.Id)}
				ref={stepRef}
				style={{
					backgroundColor: isImportedEditPreview
						? undefined
						: stepBackgroundColor,
				}}
				sx={(t) => ({
					...(getRowStyle ? getRowStyle(t) : {}),
					borderRadius: 2,
					color: infoStepTextColor,
					bgcolor: isImportedEditPreview
						? documentTitleSurfaceColor
						: isManualProcedureInfo
							? infoStepBackgroundColor
						: undefined,
					boxShadow: isImportedEditPreview || isManualProcedureInfo
						? `inset 0 0 0 1px ${alpha(t.palette.divider, 0.85)}`
						: undefined,
					px: isManualProcedureInfo ? { xs: 1.5, md: 2.5 } : 2,
					py: isManualProcedureInfo ? 1.25 : 1.5,
					alignItems: "stretch",
					justifyContent: "flex-start",
					textAlign: "left",
					minHeight: isManualProcedureInfo ? 54 : undefined,
				})}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						width: "100%",
						minWidth: 0,
					}}
				>
					<Box
						sx={{
							...richContentTableSx,
							flex: 1,
							minWidth: 0,
							...(isManualProcedureInfo && {
								display: "grid",
								gridTemplateColumns: manualChecklistGridColumns,
								columnGap: { xs: 1, md: 1.5 },
								alignItems: "center",
								"& > *": {
									gridColumn: { xs: "2 / 4", md: "2 / 6" },
									margin: 0,
								},
							}),
							"& [style*='text-align']": {
								textAlign: "left !important",
							},
						}}
					>
						<SATOSContentRender
							content={
								step.Value instanceof Element
									? undefined
									: (step.Value as string | undefined)
							}
						/>
					</Box>
					{showCommentsAndSuggestion && (
						<Box
							onClick={(e) => e.stopPropagation()}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "flex-end",
								gap: 0.5,
								flexShrink: 0,
								ml: 1,
							}}
						>
							<StepTools
								checklistId={activeSchemeId || 0}
								toggleShowMeasurements={toggleShowMeasurements}
								toggleShowStepLog={toggleShowStepLog}
								step={step}
								skipStep={handleSkipStep}
								isActiveStep={isActiveStep}
							/>
							{canToggleInfoFeedback && (
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										toggleFeedbackExpanded();
									}}
									aria-expanded={isFeedbackExpanded}
									aria-label={
										isFeedbackExpanded
											? "Collapse comments and improvements"
											: "Expand comments and improvements"
									}
									sx={{
										transform: isFeedbackExpanded ? "rotate(180deg)" : "none",
										transition: "transform 0.2s ease",
									}}
								>
									<ExpandMoreIcon fontSize="small" />
								</IconButton>
							)}
						</Box>
					)}
				</Box>
				{canToggleInfoFeedback && isFeedbackExpanded && (
					<Box sx={{ mt: 1, width: "100%", minWidth: 0 }}>
						{renderFeedbackPanels()}
					</Box>
				)}
				{showStepLog && (
					<Box sx={{ mt: 1, width: "100%" }}>
						<StepLog
							{...props}
							showStepLog={showStepLog}
							toggleShowStepLog={toggleShowStepLog}
						/>
					</Box>
				)}
			</Box>
		);
	}

	// Imported checklist – use grid-row / grid-column layout for document structure
	if (props.checklist?.ImportedChecklist) {
		const isSkippedOrOverridden = step.IsSkipped || step.IsOverridden;
		const isEditPreview = !isProcedurePage;
		const isDocumentTitleRow = isEditPreview && step.NumberOfFields === 0;
		const canToggleFeedback =
			showCommentsAndSuggestion && hasCommentsOrImprovements;
		return (
			<>
				<Box
					className={getRowTypeClass(step)}
					id={String(step.Id)}
					ref={stepRef}
					style={{
						backgroundColor: isEditPreview ? undefined : importedStepBackgroundColor,
					}}
					sx={(t) => ({
						position: "relative",
						borderRadius: 2,
						color: importedStepTextColor,
						bgcolor: isDocumentTitleRow
							? importedDocumentTitleSurfaceColor
							: undefined,
						boxShadow: isDocumentTitleRow
							? `inset 0 0 0 1px ${alpha(t.palette.divider, 0.85)}`
							: `inset 0 0 0 1px ${alpha(t.palette.divider, isDark ? 0.28 : 0.5)}`,
						...(isDocumentTitleRow && {
							"& .grid-column--chapter": {
								alignItems: "center",
								gap: 2,
								minHeight: 54,
								px: 2,
								py: 1,
							},
							"& .grid-column--chapter > div, & .grid-column--chapter > p": {
								margin: 0,
								lineHeight: 1.35,
							},
							"& .grid-column--chapter > div:first-of-type, & .grid-column--chapter > p:first-of-type": {
								flex: "0 0 44px",
								color: t.palette.text.secondary,
								fontWeight: 700,
							},
							"& .grid-column--chapter a": {
								color: t.palette.primary.main,
								fontWeight: 700,
								textDecoration: "none",
							},
						}),
						...(isSkippedOrOverridden && {
							opacity: 0.75,
							"&::before": {
								content: '""',
								position: "absolute",
								left: 0,
								top: 0,
								bottom: 0,
								width: 4,
								borderRadius: "2px 0 0 2px",
								background: step.IsSkipped
									? t.palette.mode === "dark"
										? t.palette.grey[500]
										: t.palette.grey[400]
									: t.palette.warning.main,
							},
						}),
					})}
				>
					{(step.IsSkipped || step.IsOverridden) && (
						<Box sx={{ position: "absolute", top: 8, left: 12, zIndex: 2 }}>
							<Chip
								icon={
									step.IsSkipped ? (
										<SkipNextIcon sx={{ fontSize: 14 }} />
									) : (
										<WarningIcon sx={{ fontSize: 14 }} />
									)
								}
								label={
									<Translate
										String={
											step.IsSkipped
												? "Checklist.Step.Badge.Skipped"
												: "Checklist.Step.Badge.Overridden"
										}
									>
										{step.IsSkipped ? "Skipped" : "Overridden"}
									</Translate>
								}
								size="small"
								sx={(t) => getStatusChipSx(t, Boolean(step.IsSkipped))}
							/>
						</Box>
					)}
					<Box
						sx={{ display: "flex", width: "100%", alignItems: "flex-start" }}
					>
						<Box
							className={getColumnTypeClass(step)}
							sx={{
								flex: "1 1 0",
								minWidth: 0,
								flexDirection: "row",
								fontSize: bodyFontSize,
								lineHeight: 1.55,
								color: "inherit",
							}}
						>
							<SATOSContentRender
								content={
									step.Value instanceof Element
										? undefined
										: (step.Value as string | undefined)
								}
							/>
						</Box>
						<Box
							onClick={(e) => e.stopPropagation()}
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "flex-end",
								gap: 0.5,
								flexShrink: 0,
								ml: 1,
							}}
						>
							{showCommentsAndSuggestion && (
								<Box sx={{ flexShrink: 0 }}>
									<StepTools
										checklistId={activeSchemeId || 0}
										toggleShowMeasurements={toggleShowMeasurements}
										toggleShowStepLog={toggleShowStepLog}
										step={step}
										skipStep={handleSkipStep}
										isActiveStep={isActiveStep}
									/>
								</Box>
							)}
							<Box
								sx={{ width: 32, display: "flex", justifyContent: "center" }}
							>
								{canToggleFeedback && (
									<IconButton
										size="small"
										onClick={(e) => {
											e.stopPropagation();
											toggleFeedbackExpanded();
										}}
										aria-expanded={isFeedbackExpanded}
										aria-label={
											isFeedbackExpanded
												? "Collapse comments and improvements"
												: "Expand comments and improvements"
										}
										sx={{
											transform: isFeedbackExpanded ? "rotate(180deg)" : "none",
											transition: "transform 0.2s ease",
										}}
									>
										<ExpandMoreIcon fontSize="small" />
									</IconButton>
								)}
							</Box>
						</Box>
					</Box>
					{hasMeasurement && (
						<Box sx={{ paddingLeft: 2 }}>
							<StepMeasurements
								activeSchemeId={activeSchemeId || 0}
								autoMode={autoMode}
								listenerTags={step?.ListenerTags ?? []}
								showMeasurements={showMeasurements}
								step={step}
								setShowMeasurements={setShowMeasurements}
								isActiveStep={isActiveStep}
							/>
						</Box>
					)}
					{canToggleFeedback && isFeedbackExpanded && (
						<Box sx={{ marginRight: 2, minWidth: 0 }}>
							{renderFeedbackPanels()}
						</Box>
					)}
					{showStepLog && (
						<Box sx={{ marginRight: 2 }}>
							<StepLog
								{...props}
								showStepLog={showStepLog}
								toggleShowStepLog={toggleShowStepLog}
							/>
						</Box>
					)}
				</Box>
			</>
		);
	}

	// Manual checklist – card layout
	const isSkippedOrOverridden = step.IsSkipped || step.IsOverridden;
	const shadowColor = alpha(
		getSurfaceBorderColor(theme, "subtle"),
		0.9,
	);
	const highlightBorder =
		getSurfaceBorderColor(theme, "strong");
	const responsibleGroupName =
		step.GroupId != null && step.GroupId > 0
			? groups?.find((g) => g.Id === step.GroupId)?.Name
			: undefined;
	const completedByUserName = step.UserName?.trim() || undefined;
	const showCompletedByUser = isCompleted && Boolean(completedByUserName);
	const ansvarLabel = responsibleGroupName
		? showCompletedByUser
			? `${responsibleGroupName} - ${completedByUserName}`
			: responsibleGroupName
		: completedByUserName;
	const canToggleFeedback =
		showCommentsAndSuggestion && hasCommentsOrImprovements;

	return (
		<>
			<Box
				id={String(step.Id)}
				ref={stepRef}
				style={{ backgroundColor: stepBackgroundColor }}
				sx={(t) => ({
					position: "relative",
					borderRadius: 2,
					color: stepTextColor,
					bgcolor: stepBackgroundColor ? undefined : "background.paper",
					boxShadow: `inset 0 0 0 1px ${shadowColor}`,
					transition:
						"box-shadow .18s ease, transform .12s ease, background-color .18s ease",
					cursor: "default",
					...(isSkippedOrOverridden && {
						opacity: 0.75,
						"&::before": {
							content: '""',
							position: "absolute",
							left: 0,
							top: 0,
							bottom: 0,
							width: 4,
							borderRadius: "2px 0 0 2px",
							background: step.IsSkipped
								? t.palette.mode === "dark"
									? t.palette.grey[500]
									: t.palette.grey[400]
								: t.palette.warning.main,
						},
					}),
					...(isHighlighted && {
						boxShadow: `inset 0 0 0 1.5px ${highlightBorder}`,
						transform: "translateY(-1px)",
					}),
					"&:hover": {
						boxShadow: isHighlighted
							? `inset 0 0 0 1.5px ${highlightBorder}`
							: `inset 0 0 0 1px ${alpha(
									t.palette.mode === "light"
										? t.palette.common.black
										: t.palette.common.white,
									0.1,
								)}`,
					},
				})}
			>
				{/* Skipped / Blocked badge */}
				{(step.IsSkipped || step.IsOverridden) && (
					<Box sx={{ position: "absolute", top: 8, left: 12, zIndex: 2 }}>
						<Chip
							icon={
								step.IsSkipped ? (
									<SkipNextIcon sx={{ fontSize: 14 }} />
								) : (
									<WarningIcon sx={{ fontSize: 14 }} />
								)
							}
							label={
								<Translate
									String={
										step.IsSkipped
											? "Checklist.Step.Badge.Skipped"
											: "Checklist.Step.Badge.Overridden"
									}
								>
									{step.IsSkipped ? "Skipped" : "Overridden"}
								</Translate>
							}
							size="small"
							sx={(t) => getStatusChipSx(t, Boolean(step.IsSkipped))}
						/>
					</Box>
				)}

				{/* Table-like row: Nr | Aksjon | Ansvar | Kommentar | Sign */}
				<Box
					className="step-row-grid"
					sx={{
						display: "grid",
						gridTemplateColumns: manualChecklistGridColumns,
						columnGap: { xs: 1, md: 1.5 },
						alignItems: "center",
						px: { xs: 1.5, md: 2.5 },
						py: 1.25,
						minHeight: 62,
					}}
				>
					{/* Nr column */}
					<Box sx={{ textAlign: "right" }}>
						{displayStepNumber != null && (
							<Typography
								sx={{
									fontWeight: 700,
									fontSize: headingFontSize,
									color: "text.secondary",
								}}
							>
								{displayStepNumber}.
							</Typography>
						)}
					</Box>

					{/* Aksjon column */}
					<Box
						sx={{
							minWidth: 0,
							fontSize: bodyFontSize,
							lineHeight: 1.5,
						}}
					>
						<Box sx={richContentTableSx}>
							<SATOSContentRender
								content={
									step.Value instanceof Element
										? undefined
										: (step.Value as string | undefined)
								}
							/>
						</Box>
					</Box>

					{/* Ansvar column: responsible group; when completed, append who checked it */}
					<Box className="step-col-ansvar" sx={{ display: { xs: "none", md: "block" } }}>
						{ansvarLabel ? (
							<Typography variant="caption" sx={{ color: "text.secondary" }}>
								{ansvarLabel}
							</Typography>
						) : null}
					</Box>

					{/* Kommentar column */}
					<Box
						className="step-col-comment"
						sx={{
							display: { xs: "none", md: "flex" },
							alignItems: "center",
							justifyContent: "center",
							gap: 0.5,
						}}
					>
						{commentIndicator && commentIndicator.count > 0 ? (
							<Tooltip title="Click to review" arrow>
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										commentIndicator.onClick();
									}}
									aria-label={`${commentIndicator.count} suggestion(s)`}
									sx={{
										color: isHighlighted ? "info.main" : "text.secondary",
							bgcolor: isHighlighted
								? alpha(theme.palette.info.main, 0.12)
								: "transparent",
									}}
								>
									<ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
								</IconButton>
							</Tooltip>
						) : showCommentsAndSuggestion && hasCommentsOrImprovements ? (
							<>
								{commentCount > 0 && (
									<Tooltip
										title={
											<Translate String="Procedure.Checklist.Comments">
												Comments
											</Translate>
										}
										arrow
									>
										<Box
											sx={{
												display: "inline-flex",
												alignItems: "center",
												gap: 0.25,
												color: "info.main",
											}}
										>
											<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
											<Typography variant="caption" fontWeight={600}>
												{commentCount}
											</Typography>
										</Box>
									</Tooltip>
								)}
								{improvementCount > 0 && (
									<Tooltip
										title={
											<Translate String="Procedure.Checklist.Improvements">
												Improvements
											</Translate>
										}
										arrow
									>
										<Box
											sx={{
												display: "inline-flex",
												alignItems: "center",
												gap: 0.25,
												color: "warning.main",
											}}
										>
											<LightbulbOutlinedIcon sx={{ fontSize: 16 }} />
											<Typography variant="caption" fontWeight={600}>
												{improvementCount}
											</Typography>
										</Box>
									</Tooltip>
								)}
							</>
						) : null}
					</Box>

					{/* Sign column: status + menu + expand (left-aligned) */}
					<Box
						onClick={(e) => e.stopPropagation()}
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							gap: 0.5,
						}}
					>
						{showCommentsAndSuggestion && (
							<StepTools
								checklistId={activeSchemeId || 0}
								toggleShowMeasurements={toggleShowMeasurements}
								toggleShowStepLog={toggleShowStepLog}
								step={step}
								skipStep={handleSkipStep}
								isActiveStep={isActiveStep}
							/>
						)}
						{canToggleFeedback && (
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									toggleFeedbackExpanded();
								}}
								aria-expanded={isFeedbackExpanded}
								aria-label={
									isFeedbackExpanded
										? "Collapse comments and improvements"
										: "Expand comments and improvements"
								}
								sx={{
									transform: isFeedbackExpanded ? "rotate(180deg)" : "none",
									transition: "transform 0.2s ease",
								}}
							>
								<ExpandMoreIcon fontSize="small" />
							</IconButton>
						)}
					</Box>
				</Box>

				{hasMeasurement && (
					<Box sx={{ px: 2, pb: 1 }}>
						<StepMeasurements
							activeSchemeId={activeSchemeId || 0}
							autoMode={autoMode}
							listenerTags={step?.ListenerTags ?? []}
							showMeasurements={showMeasurements}
							step={step}
							setShowMeasurements={setShowMeasurements}
							isActiveStep={isActiveStep}
						/>
					</Box>
				)}

				{/* Expanded: comments, improvements, actions */}
				{canToggleFeedback && isFeedbackExpanded && (
					<Box
						sx={{
							px: 2,
							pb: 2,
							pt: 0,
							borderTop: (t) => getSurfaceBorderStyle(t, "subtle"),
							mt: 0,
							ml: manualChecklistStepNumber ? 40 : 0,
						}}
					>
						{renderFeedbackPanels()}
					</Box>
				)}

				{showStepLog && (
					<Box
						sx={{
							px: 2,
							pb: 2,
							borderTop: (t) => getSurfaceBorderStyle(t, "subtle"),
						}}
					>
						<StepLog
							{...props}
							showStepLog={showStepLog}
							toggleShowStepLog={toggleShowStepLog}
						/>
					</Box>
				)}
			</Box>
		</>
	);
};
